import { useState, useEffect, useRef, useLayoutEffect, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeaderActions from '../../../components/feature/HeaderActions';
import { PATHS } from '../../../router/paths';

export default function Navbar() {
  const { t } = useTranslation();
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [introStarted, setIntroStarted] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const introTitle = t('nav.introTitle');
  const introCharacters = Array.from(introTitle);
  const introBoardExitDelay = 3.15;
  const introBoardExitDuration = 1.05;
  const introContentDelay = 4.15;
  const introFadeDuration = 0.7;
  const introTotalDuration = 5600;

  // Refs for direct DOM manipulation — bypasses React re-renders on scroll
  const navRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const logoIconRef = useRef<HTMLElement>(null);
  const brandTextRef = useRef<HTMLSpanElement>(null);
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const headerActionsWrapperRef = useRef<HTMLDivElement>(null);
  const isMobileRef = useRef(false);

  // Keep mobile ref in sync with state
  useEffect(() => {
    isMobileRef.current = isMobileViewport;
  }, [isMobileViewport]);

  // Apply scroll progress directly to DOM — zero React re-renders
  const applyScrollProgress = (p: number) => {
    const m = isMobileRef.current;

    if (navRef.current) {
      navRef.current.style.paddingTop = `${(m ? 12 : 24) * (1 - p)}px`;
    }
    if (containerRef.current) {
      const topR = (m ? 22 : 28) * (1 - p);
      const botR = (m ? 22 : 28) - p * (m ? 2 : 4);
      containerRef.current.style.backgroundColor = `rgba(13,13,13,${0.66 + p * 0.08})`;
      containerRef.current.style.borderTopLeftRadius = `${topR}px`;
      containerRef.current.style.borderTopRightRadius = `${topR}px`;
      containerRef.current.style.borderBottomLeftRadius = `${botR}px`;
      containerRef.current.style.borderBottomRightRadius = `${botR}px`;
    }
    if (rowRef.current) {
      const pad = (m ? 12 : 16) - p * (m ? 5 : 10);
      rowRef.current.style.paddingTop = `${pad}px`;
      rowRef.current.style.paddingBottom = `${pad}px`;
    }
    if (logoContainerRef.current) {
      const sz = (m ? 42 : 56) - p * (m ? 8 : 20);
      const r = (m ? 16 : 20) - p * (m ? 4 : 8);
      logoContainerRef.current.style.width = `${sz}px`;
      logoContainerRef.current.style.height = `${sz}px`;
      logoContainerRef.current.style.borderRadius = `${r}px`;
    }
    if (logoIconRef.current) {
      logoIconRef.current.style.fontSize = `${(m ? 20 : 28) - p * (m ? 2 : 4)}px`;
    }
    if (brandTextRef.current) {
      brandTextRef.current.style.fontSize = `${(m ? 17 : 24) - p * (m ? 1.5 : 4)}px`;
    }
    const scale = 1.08 - p * 0.16;
    if (desktopNavRef.current) {
      desktopNavRef.current.style.transform = `scale(${scale})`;
    }
    if (headerActionsWrapperRef.current) {
      headerActionsWrapperRef.current.style.transform = `scale(${scale})`;
      headerActionsWrapperRef.current.style.transformOrigin = 'right center';
    }
  };

  // Apply initial styles before first paint
  useLayoutEffect(() => {
    applyScrollProgress(Math.min(window.scrollY / 180, 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply when viewport changes (mobile/desktop switch)
  useEffect(() => {
    applyScrollProgress(Math.min(window.scrollY / 180, 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileViewport]);

  // Scroll listener — updates DOM directly, no setState
  useEffect(() => {
    let frameId = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        applyScrollProgress(Math.min(window.scrollY / 180, 1));
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1199px)');

    const syncViewport = () => {
      const mobile = mediaQuery.matches;
      setIsMobileViewport(mobile);

      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);

    return () => {
      mediaQuery.removeEventListener('change', syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport || !isMobileMenuOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen, isMobileViewport]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIntroStarted(true);
      setIntroDone(true);
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setIntroStarted(true);
    });
    const timeoutId = window.setTimeout(() => {
      setIntroDone(true);
    }, introTotalDuration);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [introTotalDuration]);

  const introBoardOpacity = introDone ? 0 : 1;
  const introBoardAnimation =
    introStarted && !introDone
      ? `navbarFlipBoardExit ${introBoardExitDuration}s cubic-bezier(0.22, 1, 0.36, 1) ${introBoardExitDelay}s forwards`
      : 'none';
  const introContentTransform = introStarted ? 'translateY(0%)' : 'translateY(150%)';
  const introContentOpacity = introStarted ? 1 : 0;
  const introContentFilter = introStarted ? 'blur(0px)' : 'blur(10px)';
  const navItems = [
    { href: PATHS.home, label: t('nav.home') },
    { href: PATHS.homeSluzby, label: t('nav.services') },
    { href: PATHS.cennik, label: t('nav.pricing') },
    { href: PATHS.omne, label: t('nav.about') },
    { href: PATHS.kontakt, label: t('nav.contact') },
  ];

  return (
    <nav
      ref={navRef}
      className="fixed inset-x-0 top-0 z-50 px-4 sm:px-6"
    >
      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl overflow-hidden border border-white/[0.12] px-4 shadow-[0_24px_70px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-[28px] backdrop-saturate-150 sm:px-6"
      >
        <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
          <div
            aria-hidden="true"
            className="navbar-flip-board"
            style={{
              opacity: introBoardOpacity,
              animation: introBoardAnimation,
              transition: `opacity ${introFadeDuration}s ease`,
            }}
          >
            {introCharacters.map((character, index) => {
              if (character === ' ') {
                return <span key={`space-${index}`} className="navbar-flip-space" />;
              }

              return (
                <span
                  key={`${character}-${index}`}
                  className="navbar-flip-cell"
                  style={{ ['--flip-index' as string]: index } as CSSProperties}
                >
                  <span className="navbar-flip-cell-half navbar-flip-cell-half--top" aria-hidden="true">
                    <span className="navbar-flip-cell-text navbar-flip-cell-text--top navbar-flip-cell-text--final">
                      {character}
                    </span>
                  </span>
                  <span className="navbar-flip-cell-half navbar-flip-cell-half--bottom" aria-hidden="true">
                    <span className="navbar-flip-cell-text navbar-flip-cell-text--bottom navbar-flip-cell-text--final">
                      {character}
                    </span>
                  </span>
                  <span className="navbar-flip-cell-flap" aria-hidden="true">
                    <span className="navbar-flip-cell-flap-face navbar-flip-cell-flap-face--front">
                      <span className="navbar-flip-cell-text navbar-flip-cell-text--top navbar-flip-cell-text--placeholder" />
                    </span>
                    <span className="navbar-flip-cell-flap-face navbar-flip-cell-flap-face--back">
                      <span className="navbar-flip-cell-text navbar-flip-cell-text--bottom">{character}</span>
                    </span>
                  </span>
                </span>
              );
            })}
          </div>
        </div>

        <div
          className="relative z-20"
          style={{
            transform: introContentTransform,
            opacity: introContentOpacity,
            filter: introContentFilter,
            transition:
              `transform 1.05s cubic-bezier(0.22, 1, 0.36, 1) ${introContentDelay}s, opacity 0.82s ease ${introContentDelay}s, filter 0.82s ease ${introContentDelay}s`,
          }}
        >
          <div
            ref={rowRef}
            className="flex items-center justify-between gap-4"
          >
            {/* Logo */}
            <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3 group cursor-pointer">
              <div
                ref={logoContainerRef}
                className="relative flex items-center justify-center border border-brand-green/25 bg-brand-green/15"
              >
                <i ref={logoIconRef} className="ri-line-chart-line text-brand-green"></i>
              </div>
              <span
                ref={brandTextRef}
                className="font-caprasimo text-white group-hover:text-brand-green transition-colors whitespace-nowrap"
                style={{ lineHeight: 1 }}
              >
                {t('nav.brand')}
              </span>
            </Link>

            <div ref={desktopNavRef} className="hidden min-[1200px]:flex items-center gap-2 rounded-full bg-black/10 px-3 py-2" style={{ transformOrigin: 'center' }}>
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="rounded-full px-4 py-2 text-sm font-medium text-white/76 hover:bg-white/8 hover:text-white transition-all cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div ref={headerActionsWrapperRef} className="hidden min-[1200px]:block">
              <HeaderActions scale={1} />
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.05] text-white transition-all hover:bg-white/[0.08] min-[1200px]:hidden"
              aria-label={isMobileMenuOpen ? t('modal.close') : t('nav.menu')}
              aria-expanded={isMobileMenuOpen}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-[1.15rem]`} />
            </button>
          </div>

          {isMobileViewport && isMobileMenuOpen ? (
            <div className="border-t border-white/10 pb-5 pt-4 min-[1200px]:hidden">
              <div className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white/84 transition-colors hover:bg-white/[0.07] hover:text-white"
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              <div className="mt-4">
                <HeaderActions
                  scale={1}
                  orientation="column"
                  fullWidth
                  className="w-full"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
